import { HttpContext, HttpParams, HttpResponse } from '@angular/common/http';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PdfViewerDialogComponent } from '@app/_dialogs/pdf-viewer-dialog/pdf-viewer-dialog.component';
import { formatDateYYYYMMDD } from '@app/_helpers/functions/date-functions';
import { AvailableServiceProvidersDto } from '@app/_models/availableServiceProvidersDto';
import { CloneInvoiceDto } from '@app/_models/cloneInvoiceDto';
import { CommandResult } from '@app/_models/command/commandResult';
import { CommandResultWithValue } from '@app/_models/command/commandResultWithValue';
import { CreateInvoiceRequestDto } from '@app/_models/createInvoiceRequestDto';
import { DelegationUserInformation } from '@app/_models/delegationDto';
import { InvoiceCatalogInformationDto } from '@app/_models/invoiceCatalogInformationDto';
import { BesrGenerationResultDto, InvoiceDto } from '@app/_models/invoiceDto';
import { InvoicesExcelExportDto } from '@app/_models/InvoicesExcelExportDto';
import { SessionPositionSuggestionDto } from '@app/_models/sessionPositionSuggestionDto';
import { DelegationInterceptor } from '@app/_providers/delegationinterceptor';
import { SLOW_REQUEST_TIMEOUT, TIMEOUT_TOKEN } from '@app/_providers/httpErrorInterceptor';
import { OAuthService } from 'angular-oauth2-oidc';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable } from 'rxjs';
import { BaseTableDataService } from './baseTableData.service';

@Injectable({
  providedIn: 'root',
})
export class InvoiceService extends BaseTableDataService<InvoiceDto, number> {
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly modalService = inject(BsModalService);
  private readonly oAuthService = inject(OAuthService);

  constructor() {
    super('invoice');
  }

  getCatalogInformation(invoiceId: number): Observable<InvoiceCatalogInformationDto[]> {
    return this.http.get<InvoiceCatalogInformationDto[]>(this.baseControllerUrl + `/${invoiceId}/catalog-information`);
  }

  send(invoiceId: number, ignoreWarnings = false): Observable<CommandResult> {
    let params = new HttpParams();

    if (ignoreWarnings) params = params.set('ignoreWarnings', 'true');

    return this.http.post<CommandResult>(this.baseControllerUrl + '/send', { invoiceId: invoiceId }, { params });
  }

  generateBesr(invoiceId: number): Observable<BesrGenerationResultDto> {
    return this.http.post<BesrGenerationResultDto>(
      `${this.baseControllerUrl}/${invoiceId}/generate-besr`,
      {},
      {
        context: new HttpContext().set(TIMEOUT_TOKEN, SLOW_REQUEST_TIMEOUT),
      },
    );
  }

  clone(invoiceId: number): Observable<CommandResultWithValue<number>> {
    const cloneInvoiceDto: CloneInvoiceDto = { invoiceId: invoiceId };
    return this.http.post<CommandResultWithValue<number>>(this.baseControllerUrl + '/clone', cloneInvoiceDto);
  }

  acceptTerms(): Observable<CommandResult> {
    return this.http.post<CommandResult>(this.baseControllerUrl + '/acceptTerms', undefined);
  }

  excelExport(insuranceContractChangesExcelExportDto: InvoicesExcelExportDto): Observable<HttpResponse<Blob>> {
    return this.http.post(this.baseControllerUrl + '/excelexport', insuranceContractChangesExcelExportDto, {
      responseType: 'blob',
      observe: 'response',
      context: new HttpContext().set(TIMEOUT_TOKEN, SLOW_REQUEST_TIMEOUT),
    });
  }

  openInvoicePdf(invoiceId: number, delegator?: DelegationUserInformation): void {
    this.translate
      .get('Global.Invoice')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        PdfViewerDialogComponent.showDialog(
          this.modalService,
          `${res} ${invoiceId}`,
          // delegator is usually added in the interceptor, but this is passed as a link to the pdf viewer component
          `${this.baseControllerUrl}/pdf/${invoiceId}${delegator ? `?${DelegationInterceptor.delegatorUserIdRequestParam}=${delegator.userId}` : ''}`,
          this.oAuthService.getAccessToken(),
        );
      });
  }

  downloadInvoiceXml(invoiceId: number): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.baseControllerUrl}/xml/${invoiceId}`, {
      responseType: 'blob',
      observe: 'response',
      context: new HttpContext().set(TIMEOUT_TOKEN, SLOW_REQUEST_TIMEOUT),
    });
  }

  getSessionPositionSuggestions(
    caseNumber: string,
    contractId: number,
    dateFrom: Date,
  ): Observable<SessionPositionSuggestionDto[]> {
    return this.http.get<SessionPositionSuggestionDto[]>(`${this.baseControllerUrl}/session-position-suggestions`, {
      params: { caseNumber, contractId: contractId.toString(), dateFrom: formatDateYYYYMMDD(dateFrom) },
    });
  }

  createInvoice(request: CreateInvoiceRequestDto, generateBesr: boolean): Observable<CommandResultWithValue<number>> {
    const params = new HttpParams().set('generateBesr', generateBesr);
    return this.http.post<CommandResultWithValue<number>>(this.baseControllerUrl, request, { params });
  }

  getAvailableServiceProviders(
    invoiceId: number,
    excludeInvoiceOwner = false,
  ): Observable<AvailableServiceProvidersDto> {
    return this.http.get<AvailableServiceProvidersDto>(
      `${this.baseControllerUrl}/${invoiceId}/available-service-providers?excludeInvoiceOwner=${excludeInvoiceOwner}`,
    );
  }
}
